import React, { useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid, Typography } from '@mui/material'
import { FormInput } from '@/components/form'
import { getCreateUserSchema, IUser, User, IHttpError } from '@/@data'
import PhoneCodeSelection from '@/components/selection/PhoneCodeSelection'
import RoleSelection from '@/components/selection/RoleSelection'
import InputDateField from '@/components/common/input/InputDateField'
import { useTranslator } from '@/hooks/useTranslator'
import { useNotification } from '@/hooks/useNotification'
import { HttpStatusCode } from 'axios'

interface PersonalInfoFormProps {
  employee?: IUser
  onSuccess?: () => void
  refetch?: () => void
  setOpenSetting?: (open: boolean) => void
}

const PersonalInfoForm = React.forwardRef<{ submit: () => void } | null, PersonalInfoFormProps>(
  ({ employee, onSuccess, refetch, setOpenSetting }, ref) => {
    const { __ } = useTranslator()
    const n = useNotification()
    const { useUpdateMutation } = User.objects.getCommonMutations()
    const updateUserMutation = useUpdateMutation()

    const methods = useForm<any>({
      defaultValues: {
        first_name: employee?.first_name || '',
        email: employee?.email || '',
        phone_code: employee?.phone_code || '',
        employee_id: employee?.employee_id || '',
        phone: employee?.phone || '',
        birth_date: employee?.birth_date || '',
        role_id: employee?.role_id || 0,
        company_id: employee?.company_id || 0
      },
      resolver: yupResolver(getCreateUserSchema(__)),
      mode: 'onChange'
    })

    const onSubmit = (data: IUser) => {
      if (!employee) return
      console.log('onSubmit called', data)

      const payload = {
        ...data
      }

      console.log('Submitting payload:', payload)

      updateUserMutation.mutate(
        { id: employee.id, data: payload },
        {
          onSuccess: response => {
            n.success(response.message)
            refetch?.()
            setOpenSetting?.(false)
            onSuccess?.()
          },
          onError: (error: IHttpError) => {
            const dataErr = error.message as unknown as any[]

            if (error.status === HttpStatusCode.UnprocessableEntity && error.message.length) {
              dataErr.forEach(err => {
                methods.setError(err.name, { message: err.errors.join('') })
              })

              return
            }

            n.error(error.message)
          }
        }
      )
    }

    React.useImperativeHandle(ref, () => ({
      submit: () => {
        console.log('Imperative submit called')
        methods.handleSubmit(onSubmit)()
      }
    }))

    useEffect(() => {
      if (!employee) return

      methods.reset({
        first_name: employee.first_name || '',
        email: employee.email || '',
        phone_code: employee.phone_code || '',
        phone: employee.phone || '',
        birth_date: employee.birth_date || '',
        role_id: employee.role_id || 0,
        company_id: employee.company_id || 0,
        employee_id: employee.employee_id || ''
      })
    }, [employee, methods])

    return (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid size={4}>
              <FormInput
                name='first_name'
                placeholder={__('Full Name')}
                control={methods.control}
                label={__('Full Name')}
                fullWidth
                required
              />
            </Grid>
            <Grid size={4}>
              <FormInput
                name='employee_id'
                placeholder={__('User Code')}
                control={methods.control}
                label={__('User Code')}
                fullWidth
                required
              />
            </Grid>
            <Grid size={4}>
              <Controller
                name='birth_date'
                control={methods.control}
                render={({ field }) => (
                  <InputDateField
                    value={field.value}
                    placeholder={__('Select Date')}
                    onChange={field.onChange}
                    className='rounded-full py-2'
                    label={__('D.O.B')}
                    required
                  />
                )}
              />
            </Grid>

            <Grid size={4}>
              <FormInput
                name='email'
                placeholder={__('Email')}
                control={methods.control}
                label={__('Email')}
                disabled
                fullWidth
                required
              />
            </Grid>

            <Grid size={4} gap={2}>
              <Grid size={{ xs: 12, sm: 12 }}>
                <Typography variant='body2' fontWeight={500}>
                  {__('Phone')}
                  {/* <span style={{ color: 'red' }}>*</span> */}
                </Typography>
              </Grid>
              <Grid container sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
                <Grid size={{ xs: 12, sm: 4 }} sx={{ flex: 1 }}>
                  <Controller
                    name='phone_code'
                    control={methods.control}
                    render={({ field }) => <PhoneCodeSelection {...field} placeholder={__('+1 (*)')} required />}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 7 }} sx={{ flex: 1 }} className='flex-1'>
                  <FormInput
                    name='phone'
                    placeholder={__('Enter Phone')}
                    control={methods.control}
                    type='number'
                    required
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid size={4} gap={2}>
              <Typography variant='body2' fontWeight={500}>
                {__('Role')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Controller
                name='role_id'
                control={methods.control}
                render={({ field }) => (
                  <RoleSelection
                    {...field}
                    placeholder={__('Select Role')}
                    disabled={employee && employee.company_id !== 1}
                    required
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    )
  }
)

export default PersonalInfoForm
